import React, { useEffect, useMemo, useRef, useState } from 'react';
import PrescriptionsList from './components/prescriptionsList';
import { MedicationForm, DEFAULT_VALUE_FIELD } from './components/medicationForm';
import { useTranslation } from 'react-i18next';
import _, { find, isNil } from 'lodash';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import PrescriptionPatientCheck from './prescriptionPatientCheck';
import PrescriptionOrganization from './prescriptionOrganization';
import { cis2Api, nhsPrescriptionApi } from '../../../../app/apiPrescription';
import { useDispatch, useSelector } from 'react-redux';
import { cis2gpCode, cis2nhsGpRole, cis2token, cis2idToken, cis2User } from '../../../../store/reducers/cis2';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { setSnackbar } from '../../../../store/reducers/globals';
import { Box, Divider, MenuItem, Paper, Stack, Typography } from '@mui/material';
import MedicationIcon from '@mui/icons-material/Medication';
import MedicationsList from './components/medicationsList';
import { user } from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import uuidv4 from 'uuid/v4';
import Notes from '../../../../util/notes';
import PrescriptionMainData from './prescriptionMainData';
import Cis2SessionTimeLeft from './components/cis2sessionTimeLeft';
import PrescriptionPatientWarnings from './components/prescriptionPatientWarnings';


const DEFAULT_VALUE = {
    organization: null,
    medications: [],
    validityStartDate: {
        ...{ ...DEFAULT_VALUE_FIELD },
        // value: moment(),
    },
    validityEndDate: {
        ...{ ...DEFAULT_VALUE_FIELD },
    },
    indication: '',
    courseOfTherapyType: {
        ...{ ...DEFAULT_VALUE_FIELD },
        value: 'acute',
    },
    category: {
        ...{ DEFAULT_VALUE_FIELD },
        value: 'outpatient',
    },
    instructions: {
        ...{ ...DEFAULT_VALUE_FIELD },
        value: '',
    },
    eps: null,
};

// This is the external version of Prescription Form, it integrates with NHS dm+d for drug selection
const PrescriptionFormExtended = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const controller = new AbortController();

    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    const cisToken = useSelector(cis2token);
    const cisUser = useSelector(cis2User);
    const cisGpCode = useSelector(cis2gpCode);
    const cisNhsGpRole = useSelector(cis2nhsGpRole);

    const match = useLocation();
    const history = useHistory();

    const medicationFormRef = useRef(null);
    const mainDataRef = useRef(null);

    // const [prescriptions, setPrescriptions] = useState([]);
    const [form, setForm] = useState(DEFAULT_VALUE);
    const [disabled, setDisabled] = useState(false);
    const [error, setError] = useState(false);

    const [addMedication, setAddMedication] = useState(false);

    const [medication, setMedication] = useState(null);
    const [editedMedication, setEditedMedication] = useState(null);
    const medicationPosition = useMemo(() => {
        if (editedMedication) {
            const idx = _.findIndex(form.medications, (m) => m.id === editedMedication.id);
            return idx;
        } else {
            return form.medications.length;
        }
    }, [editedMedication, form.medications]);
    const setMedications = (medications) => {
        setForm((prev) => ({
            ...prev,
            medications: medications,
        }));
    };

    useEffect(() => {
        if (!_.isNil(props.selectedHistoryForm)) {
            setForm(props.selectedHistoryForm.values);
        }
    }, [props.selectedHistoryForm]);

    const cis2DisplayName =
        [cisUser?.given_name, cisUser?.family_name, `(${cisGpCode})`].filter(Boolean).join(' ') || cisUser?.name || cisUser?.nhsid_useruid;

    useEffect(() => {
        if (props.onCustomButtons) {
            const buttons = [];
            if (!cisToken) {
                if (!_.isNil(props.selectedHistoryForm)) {
                    buttons.push(
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {t('History Form View')}
                        </Typography>,
                    );
                }
                buttons.push(
                    <AgyleButton
                        key="cis2-authentication"
                        variant="contained"
                        onClick={cis2Login}
                        disabled={!_.isNil(props.selectedHistoryForm)}
                    >
                        {t('CIS2 Login')}
                    </AgyleButton>,
                );
            } else {
                buttons.push(
                    <Cis2SessionTimeLeft
                        key="cis2-session-time-left"
                        displayFormat={`${t('Logged into CIS2 as: ')}${cis2DisplayName} ({timeLeft}) ({timeLeft2})`}
                        sx={{ pt: 1 }}
                    />,
                );
                buttons.push(
                    <AgyleButton key="cis2-logout" variant="contained" onClick={handleCis2Logout}>
                        {t('CIS2 Logout')}
                    </AgyleButton>,
                );
                if (!addMedication) {
                    buttons.push(
                        <AgyleButton key="show-medication" variant="contained" onClick={handleAddNewMedication} disabled={disabled}>
                            {t('Add new medication')}
                        </AgyleButton>,
                    );
                } else {
                    buttons.push(
                        <Divider
                            key="medication-divider"
                            orientation="vertical"
                            flexItem
                            variant="middle"
                            sx={{
                                mx: 0,
                                borderColor: 'primary.main',
                                // color: 'primary.main',
                                // borderRightWidth: 2
                            }}
                        />,
                        <AgyleButton key="cancel-medication" variant="outlined" colour="primary" onClick={handleCancelAddMedication}>
                            {t('Cancel')}
                        </AgyleButton>,
                    );
                    if (editedMedication) {
                        buttons.push(
                            <AgyleButton
                                key="edit-medication"
                                startIcon={<CheckIcon />}
                                variant="contained"
                                onClick={handleInsertMedication}
                                disabled={disabled}
                            >
                                {t('Update medication')}
                            </AgyleButton>,
                        );
                    } else {
                        buttons.push(
                            <AgyleButton
                                key="insert-medication"
                                startIcon={<AddIcon />}
                                variant="contained"
                                onClick={handleInsertMedication}
                                disabled={disabled}
                            >
                                {t('Add to prescription')}
                            </AgyleButton>,
                        );
                    }
                    buttons.push(
                        <Divider
                            key="medication-divider"
                            orientation="vertical"
                            flexItem
                            variant="middle"
                            sx={{
                                mx: 0,
                                borderColor: 'primary.main',
                                // color: 'primary.main',
                                // borderRightWidth: 2
                            }}
                        />,
                    );
                }
            }
            props.onCustomButtons(buttons);
        }

        if (props.onSubmitFunction) {
            props.onSubmitFunction(handleSave);
        }
        if (props.onCustomSubmitText) {
            props.onCustomSubmitText(t('Issue prescription'));
        }
        if (props.onDisableSubmit) {
            props.onDisableSubmit(
                props.disableSave || !isNil(props.selectedHistoryForm) || addMedication || !cisToken || form.medications.length === 0,
            );
        }
        setDisabled(props.disableSave || !cisToken || !isNil(props.selectedHistoryForm));
    }, [
        props.disableSave,
        props.selectedHistoryForm,
        addMedication,
        form.medications,
        form,
        medication,
        editedMedication,
        cisToken,
        cis2DisplayName,
    ]);

    useEffect(() => {
        if (addMedication && props._containerScrollRef?.current) {
            props._containerScrollRef.current.scrollTop = 0;
        }
    }, [addMedication, props._containerScrollRef]);

    const handleAddNewMedication = () => {
        setMedication(null);
        setAddMedication(true);
    };

    const handleInsertMedication = () => {
        if (medicationFormRef.current?.validateForm()) {
            const clone = _.cloneDeep(form.medications);
            if (editedMedication) {
                console.log('Edited medication: ', medication);
                const idx = _.findIndex(form.medications, (m) => m.id === editedMedication.id);
                clone[idx] = medication;
            } else {
                console.log('Insert medication: ', medication);
                clone.push(medication);
            }
            setMedications(clone);
            setMedication(null);
            setEditedMedication(null);
            setAddMedication(false);
        }
    };

    const handleCancelAddMedication = () => {
        console.log('cancel medication: ', medication);
        setMedication(null);
        setEditedMedication(null);
        setAddMedication(false);
    };

    // const reloadPrescriptionsList = () => {
    //     nhsPrescriptionApi
    //         .list({
    //             piwId: props.patient.__id, //'WARD-001',
    //             patientId: props.patient.patient.__id, // 'D9010020',
    //             cis2token: cisToken,
    //             signal: controller.signal,
    //         })
    //         .then((res) => {
    //             const patient_id = res.patient_id;
    //             const piw_id = res.piw_id;
    //             const count = res.count;
    //             const pres = res.prescriptions;
    //
    //             setPrescriptions(pres);
    //         })
    //         .catch((e) => {
    //             setPrescriptions([]);
    //
    //             // TODO: show error
    //         });
    // };

    const cis2Login = () => {
        const url = new URL(window.location);
        url.searchParams.set('pId', props.patient.__id);
        url.searchParams.set('formId', 'prescriptionExternal');

        const redirectUrl = cis2Api.buildLoginRedirectUrl({
            redirectUrl: url.toString(),
            // prompt: 'login',
        });
        window.location.href = redirectUrl;
        // match.replace(cis2Api.buildLoginRedirectUrl({ redirectUrl: url.toString() }));
        //history.replace({location: redirectUrl});
    };

    const handleCis2Logout = () => {
        //cis2Api.logout
        //alert('TODO: CIS2 Logout');

        const url = new URL(window.location);
        url.searchParams.set('pId', props.patient.__id);
        url.searchParams.set('formId', 'prescriptionExternal');

        // // logout without cis2Api involvement
        // const redirectUrl = cis2Api.buildLogoutRedirectUrl({ redirectUrl: url.toString() });
        // window.location.href = redirectUrl;

        cis2Api
            .logout({ idToken: cis2idToken, redirectUrl: url.toString(), signal: controller.signal })
            .then((res) => {
                if (res) {
                    if (res.status === 200) {
                        const redirectUrl = res.data.logout_url;
                        window.location.href = redirectUrl;
                        // window.open(redirectUrl, '_blank');
                        return;
                    } else if (res.status === 302) {
                        const redirectUrl = res.redirect;
                        window.location.href = redirectUrl;
                        return;
                    }
                }
                dispatch(setSnackbar({ type: 'error', message: t('Failed to logout from CIS2') }));
            })
            .catch((error) => {
                dispatch(setSnackbar({ type: 'error', message: t('Failed to logout from CIS2') }));
                console.error('error', error);
            });
    };

    useEffect(() => {
        // if (!cisToken) {
        //     // cis2Login();
        // } else if (props.patient) {
        //     // TODO load prescriptions for patient
        //     // this is a mockup
        //     reloadPrescriptionsList();
        // }

        return () => {
            controller.abort();
        };
    }, [props.patient, cisToken]);

    const handleSave = async () => {
        if (valid()) {
            let nhs = find(
                props.patient?.patient?.patientNumbers,
                (numb) => String(numb?.type).toLowerCase() === 'NhsNumber'.toLowerCase(),
            )?.number;
            nhs = props.patient.patient.nhsNumber;

            // const demoNhs = getConfig('prescriptionApi.demoNhs');
            // if (demoNhs) {
            //     nhs = demoNhs;
            // }

            const meds = [];
            _.forEach(form.medications, (med) => {
                console.log('map medication', med);

                const drug = med.ampp || med.amp || med.vmp;

                meds.push({
                    code: drug.code, //'39732311000001104',
                    display_name: drug.display, //'Amoxicillin 250mg capsules',
                    item_number: med.id, //'dd0cdc7b-db3f-4f2f-aa21-cb4d77432264',
                    form_code: med.form.code, //'385055001',
                    dosage: {
                        // text: '2 times a day for 10 days',
                        text: med.dose.value,
                        // frequency: 2,
                        frequency: med.frequency.value,
                        //period: 1,
                        period: med.duration.value,
                        period_unit: 'd',
                        route_code: med.route.code, //'26643006',
                        route_display: med.route.value, // 'Oral',
                        sequence: meds.length + 1,
                    },
                    dispense: {
                        quantity: med.quantity.value, // should be a number
                        unit: med.unitDoseUnit?.value, // 'tablet'
                        unit_code: med.unitDoseUnit?.code, //'428673006',
                        duration: med.duration.value, // 10
                        performer: form.organization?.id || '', // 'VNE51',
                    },
                });
            });

            const data = {
                piw_id: props.patient.__id,
                patient: {
                    patient_id: props.patient.patient.__id,
                    nhs_number: nhs,
                    given_name: props.patient.patient.firstName?.toUpperCase(),
                    family_name: props.patient.patient.familyName?.toUpperCase(),
                    date_of_birth: moment(props.patient.patient.dob).format('YYYY-MM-DD'), // '1948-04-30',
                    gender: props.patient.patient.gender.toLowerCase(), //'female',
                    address_lines: props.patient.patient.addressLines.filter((line) => String(line ?? '').trim() !== ''),
                    postal_code: props.patient.patient.postalCode,
                },
                practitioner: {
                    gmc_number: cisNhsGpRole.org_code, //'C6095103',
                    professional_id: cisUser.nhsid_useruid, //'555086689106',
                    family_name: cisUser.family_name, // 'BOIN',
                    given_name: cisUser.given_name, // 'C',
                    // prefix: 'DR', // ??
                    role_profile_id: cisNhsGpRole.person_roleid, // '200102238987', // person_orgid?
                    // qualification: 'MD', // ?
                    // telecom: {
                    //     system: 'phone',
                    //     use: 'work',
                    //     value: '01234567890',
                    // },

                    // gmc_number: 'C6095103',
                    // professional_id: '555086689106',
                    // family_name: 'BOIN',
                    // given_name: 'C',
                    // // prefix: 'DR', // ??
                    // role_profile_id: '200102238987',
                },
                medications: meds,
                indication: null, //'Bacterial infection', // it's not used on EPD API side // Clinical indication for prescription
                course_of_therapy_type: form.courseOfTherapyType.value, // 'acute', // Type of therapy (continuous, acute)
                validity_period_start: form.validityStartDate.value ? moment(form.validityStartDate.value).format('YYYY-MM-DD') : null, //When prescription starts
                validity_period_end: form.validityEndDate.value ? moment(form.validityEndDate.value).format('YYYY-MM-DD') : null, //When prescription ends
                receiver_ods_code: form.organization?.id, //'VNE51', //ODS code of the receiver
                receiver_name: form.organization?.display || form.organization?.name, //'The Simple Pharmacy', //Name of the receiver
                // organization_ods: '',
                // organization_name: '',
                // organization_tel: '',

                // token_cis2: 'JPDp7JKjfxGUXq1gnGZj3nwi1zYw', // cisToken, //Token for CIS2 authentication
                token_cis2: cisToken, // 'JPDp7JKjfxGUXq1gnGZj3nwi1zYw',  //Token for CIS2 authentication
            };

            // const data = {
            //     piw_id: 'WARD-001',
            //     patient: {
            //         patient_id: 'D9010020',
            //         nhs_number: '9449304130',
            //         given_name: 'STACEY',
            //         family_name: 'TWITCHETT',
            //         date_of_birth: '1948-04-30',
            //         gender: 'female',
            //         address_lines: ['10 HEATHFIELD', 'COBHAM', 'SURREY'],
            //         postal_code: 'KT11 2QY',
            //     },
            //     practitioner: {
            //         gmc_number: 'C6095103',
            //         professional_id: '555086689106',
            //         family_name: 'BOIN',
            //         given_name: 'C',
            //         prefix: 'DR',
            //         role_profile_id: '200102238987',
            //         qualification: 'MD',
            //         telecom: {
            //             system: 'phone',
            //             use: 'work',
            //             value: '01234567890',
            //         },
            //     },
            //     medications: [
            //         {
            //             code: '39732311000001104',
            //             display_name: 'Amoxicillin 250mg capsules',
            //             item_number: 'dd0cdc7b-db3f-4f2f-aa21-cb4d77432264',
            //             form_code: '385055001',
            //             dosage: {
            //                 text: '2 times a day for 10 days',
            //                 frequency: 2,
            //                 period: 1,
            //                 period_unit: 'd',
            //                 route_code: '26643006',
            //                 route_display: 'Oral',
            //                 sequence: 1,
            //             },
            //             dispense: {
            //                 quantity: 20,
            //                 unit: 'tablet',
            //                 unit_code: '428673006',
            //                 duration: 10,
            //                 performer: 'VNE51',
            //             },
            //         },
            //     ],
            //     indication: 'Bacterial infection',
            //     course_of_therapy_type: 'acute',
            //     // validity_period_start: '2024-03-05',
            //     token_cis2: cisToken, //'eyJhbGciOi',
            // };

            const savePromise = new Promise((resolve) => {
                nhsPrescriptionApi
                    .create({ prescription: data, signal: controller.signal, stopRetry: true })
                    .then((res) => {
                        // TODO: analyze/store response
                        // This is a FAKE response
                        res = {
                            prescription_id: uuidv4() + '', //"string",
                            status: 'string',
                            timestamp: moment().toISOString(), //'2026-05-06T09:06:55.112Z',
                            message: 'string',
                            fhir_reference: 'string',
                        };

                        let clone = _.cloneDeep(form);
                        clone.printCount += 1;
                        clone.lastPrintDate = moment().toISOString();
                        clone.lastPrintBy = userObject?.userName;

                        clone.eps = res;

                        // const id = uuidv4() + '';
                        const id = res.prescription_id || uuidv4() + '';
                        const data = [
                            {
                                name: 'studies.prescriptionExternal',
                                value: {
                                    id: id,
                                    date: moment().toISOString(),
                                    who: userObject?.userName,
                                    by: userFullName,
                                    values: clone,
                                    parentForm: props.parentForm,
                                },
                                noteToAddWithRefId: Notes.getPrescriptionExternalNote(clone),
                                idToTrack: id,
                                referenceId: id,
                            },
                        ];

                        if (props.onSave) props.onSave(data);
                        if (props.onChange) props.onChange(data);

                        // if (props.onClose) props.onClose();

                        //reloadPrescriptionsList();
                        dispatch(setSnackbar({ type: 'success', message: t('Prescription issued successfully') }));

                        resolve(true);
                    })
                    .catch((error) => {
                        dispatch(setSnackbar({ type: 'error', message: t('Failed to issue a prescription') }));
                        console.error('error', error);

                        // TODO: @jz remove this code blok after EPS starts work
                        // FAKE
                        if (false) {
                            const res = {
                                prescription_id: uuidv4() + '', //"string",
                                status: 'string',
                                timestamp: moment().toISOString(), //'2026-05-06T09:06:55.112Z',
                                message: 'string',
                                fhir_reference: 'string',
                            };

                            let clone = _.cloneDeep(form);
                            clone.printCount += 1;
                            clone.lastPrintDate = moment().toISOString();
                            clone.lastPrintBy = userObject?.userName;

                            clone.eps = res;

                            // const id = uuidv4() + '';
                            const id = res.prescription_id || uuidv4() + '';
                            const data = [
                                {
                                    name: 'studies.prescriptionExternal',
                                    value: {
                                        id: id,
                                        date: moment().toISOString(),
                                        who: userObject?.userName,
                                        by: userFullName,
                                        values: clone,
                                        parentForm: props.parentForm,
                                    },
                                    noteToAddWithRefId: Notes.getPrescriptionExternalNote(clone),
                                    idToTrack: id,
                                    referenceId: id,
                                },
                            ];

                            if (props.onSave) props.onSave(data);
                            if (props.onChange) props.onChange(data);

                            // if (props.onClose) props.onClose();
                        }

                        resolve(false);
                    });
            });

            const saved = await savePromise;

            return saved;
        } else {
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: t('Invalid prescription data'),
                }),
            );
            return false;
        }
    };

    if (!cisToken && _.isNil(props.selectedHistoryForm)) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 6,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'divider',
                    backgroundColor: 'transparent',
                }}
            >
                <MedicationIcon style={{ fontSize: 48 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('CIS2 authentication required')}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                    {t('System will redirect you to the CIS2 login page after pressing login button.')}
                </Typography>
                <Box
                    sx={{
                        pt: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: 'text.secondary',
                    }}
                >
                    <AgyleButton key="cis2-authentication" variant="contained" onClick={cis2Login}>
                        {t('CIS2 Login')}
                    </AgyleButton>
                </Box>
            </Paper>
        );
    }

    const handleMedicationListAction = ({ action, data }) => {
        if (action === 'delete' && data) {
            let clone = _.cloneDeep(form.medications);
            clone = _.filter(clone, (m) => m.id !== data.id);
            setMedications(clone);
        } else if ((action === 'edit' || action === 'prev') && data) {
            const med = _.find(form.medications, (m) => m.id === data.id);
            console.log('editMedication', med);
            setEditedMedication(med);
            setAddMedication(true);
        }
    };

    const valid = () => {
        const mainDataValid = mainDataRef.current?.validateForm();

        const isValid = mainDataValid && form.medications.length > 0;
        setError(!isValid);
        return isValid;
    };

    return (
        <>
            {_.isNil(props.selectedHistoryForm) && <PrescriptionPatientCheck patient={props.patient} closeForm={props._onForceClose} />}

            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: '1px dashed',
                    borderColor: 'divider',
                    backgroundColor: 'transparent',
                }}
            >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    {cisToken ? t('Logged into CIS2 as: ') + cis2DisplayName : t('Not logged into CIS2')}
                </Typography>

                {!_.isNil(props.selectedHistoryForm) && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('History Form View')}
                    </Typography>
                )}
            </Paper>

            {!addMedication && (
                <>
                    <PrescriptionOrganization value={form.organization} handleChange={setForm} disabled={disabled} />
                    {/*<PrescriptionsList prescriptions={prescriptions} />*/}
                    <PrescriptionMainData ref={mainDataRef} form={form} handleChange={setForm} disabled={disabled} />

                    <MedicationsList medications={form.medications} disabled={disabled} onAction={handleMedicationListAction} />

                    <PrescriptionPatientWarnings patient={props.patient} />
                </>
            )}

            <MedicationForm
                ref={medicationFormRef}
                open={addMedication}
                patient={props.patient}
                disabled={disabled}
                pos={medicationPosition}
                value={editedMedication}
                onChange={(value) => {
                    console.log('set medication', value);
                    setMedication(value);
                }}
            />
        </>
    );
};

export default PrescriptionFormExtended;
